import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../store";
import {
  getSuggestions,
  clearSuggestions,
} from "../../../../Redux/Search_apis/slice";
import { SuggestionResponseDto } from "../../../../Redux/Search_apis/types";
import {
  recordActivity,
  getRecentSearches,
} from "../../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../../Redux/ActivityApi/types";

interface MobileSearchInputProps {
  placeholder?: string;
  inputGroupClass?: string;
}

const MobileSearchInput: React.FC<MobileSearchInputProps> = ({
  placeholder = "Search for Products",
  inputGroupClass = "input-group input-group-lg bg-white border-radius-lg border-0 d-flex align-items-center",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const suggestions = useSelector(
    (state: RootState) => state.search.suggestions.data
  );
  const recentSearches = useSelector(
    (state: RootState) => state.activity.recentSearches
  );
  const suggestionRef = useRef<HTMLDivElement>(null);

  const loadRecentSearches = () => {
    dispatch(getRecentSearches(5));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        dispatch(getSuggestions(searchTerm));
        setShowSuggestions(true);
      } else {
        dispatch(clearSuggestions());
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = async (q: string) => {
    if (q.trim()) {
      await dispatch(
        recordActivity({
          activities: {
            eventType: ActivityType.SEARCH,
            searchQuery: q.trim(),
          },
        })
      );
      const params = new URLSearchParams({ q: q.trim() });
      navigate(`/Categories/All?${params.toString()}`);
      setShowSuggestions(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch(searchTerm);
  };

  return (
    <div ref={suggestionRef} className="w-100 position-relative">
      <div className={inputGroupClass}>
        <span
          className="p-2 ms-1 flex-shrink-0 cursor-pointer d-flex align-items-center"
          onClick={() => handleSearch(searchTerm)}
        >
          <i className="bi bi-search text-black text-lg opacity-6"></i>
        </span>
        <input
          type="text"
          className="form-control text-md border-radius-xl search-input border-0"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setShowSuggestions(true);
            loadRecentSearches();
          }}
          style={{ boxShadow: "none" }}
        />

        <div className="d-flex align-items-center px-2 flex-shrink-0 mt-1">
          <a href="/">
            <i className="fi fi-rr-camera-viewfinder me-3 text-dark fs-4 opacity-6"></i>
          </a>
          <a href="/">
            <i className="fi fi-rr-microphone text-dark fs-4 opacity-6"></i>
          </a>
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions &&
        (suggestions.length > 0 ||
          (searchTerm.trim().length === 0 && recentSearches.length > 0)) && (
          <div
            className="position-absolute top-100 start-0 w-100 bg-white shadow-lg border-radius-lg mt-1 z-index-3 overflow-hidden text-start"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {searchTerm.trim().length > 1
              ? suggestions.map(
                  (suggestion: SuggestionResponseDto, index: number) => (
                    <div
                      key={index}
                      className="p-3 border-bottom border-light cursor-pointer hover-bg-light d-flex align-items-center"
                      onClick={() => {
                        setSearchTerm(suggestion.text);
                        handleSearch(suggestion.text);
                      }}
                    >
                      <i className="bi bi-search text-muted me-3"></i>
                      <div>
                        <h6 className="mb-0 text-sm fw-bold">
                          {suggestion.text}
                        </h6>
                        {suggestion.metadata?.categoryHierarchy?.category && (
                          <small className="text-xs text-muted">
                            In {suggestion.metadata.categoryHierarchy.category}
                          </small>
                        )}
                      </div>
                    </div>
                  )
                )
              : recentSearches.length > 0 && (
                  <>
                    <div className="p-2 px-3 border-bottom bg-light">
                      <small className="text-uppercase text-muted fw-bold text-xs">
                        Recent Searches
                      </small>
                    </div>
                    {recentSearches.map((search: string, index: number) => (
                      <div
                        key={index}
                        className="p-3 border-bottom border-light cursor-pointer hover-bg-light d-flex align-items-center"
                        onClick={() => {
                          setSearchTerm(search);
                          handleSearch(search);
                        }}
                      >
                        <i className="bi bi-clock-history text-muted me-3"></i>
                        <h6 className="mb-0 text-sm">{search}</h6>
                      </div>
                    ))}
                  </>
                )}
          </div>
        )}
    </div>
  );
};

export default MobileSearchInput;
